/*!
 * ETAR theme v4.2.4 — Product grid and product card
 * Developed by FACTICIED · https://www.facticied.com
 */
(() => {
  if (customElements.get('et-products')) return;
  const routes = () => window.EtarRoutes || {};

  class EtProducts extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.toast = this.querySelector('[data-toast]');

      this.io = new IntersectionObserver((en) => { if (en.some((e) => e.isIntersecting)) { this.classList.add('is-in'); this.io.disconnect(); } }, { threshold: 0.12 });
      if (this.dataset.recs) this.loadRecs();
      this.io.observe(this);

      // Tabs
      this.tabs.forEach((t) => t.addEventListener('click', () => this.show(+t.dataset.tab), opt));
      this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const cur = this.tabs.findIndex((t) => t.classList.contains('is-on'));
        const n = (cur + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.show(n); this.tabs[n].focus();
      }, opt);

      // Slider arrows
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.slide(-1), opt);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.slide(1), opt);
      this.panels.forEach((p) => p.querySelector('[data-track]')?.addEventListener('scroll', () => this.arrows(), { passive: true, signal: this.ac.signal }));
      this.arrows();

      // Colour dots swap the front photo
      this.addEventListener('click', (e) => {
        const sw = e.target.closest('.et-pc__sw');
        if (!sw) return;
        const card = sw.closest('.et-pc');
        card.querySelectorAll('.et-pc__sw').forEach((x) => x.classList.toggle('is-on', x === sw));
        const img = card.querySelector('.et-pc__face--front img');
        if (img && sw.dataset.img) { img.srcset = ''; img.src = sw.dataset.img; }
      }, opt);

      // Quick add is handled for every card by et-cart.js ([data-qa])

      // Theme editor: open the selected tab
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.indexOf(e.target);
        if (i > -1) this.show(i);
      }, opt);
    }

    disconnectedCallback() { this.ac?.abort(); this.io?.disconnect(); clearTimeout(this.toastT); }

    // Related products are fetched after the page loads; hide the section when there are none.
    async loadRecs() {
      try {
        const res = await fetch(this.dataset.recs);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('et-products');
        const grid = fresh && fresh.querySelector('[data-track]');
        if (!grid || !grid.children.length) { (this.closest('.shopify-section') || this).hidden = true; return; }
        this.querySelector('[data-track]').replaceWith(grid);
        this.arrows();
      } catch (e) { /* keep what is there */ }
    }

    show(i) {
      this.tabs.forEach((t, n) => { t.classList.toggle('is-on', n === i); t.setAttribute('aria-selected', String(n === i)); t.tabIndex = n === i ? 0 : -1; });
      this.panels.forEach((p, n) => { p.hidden = n !== i; p.classList.toggle('is-on', n === i); });
      this.arrows();
    }

    track() { return this.panels.find((p) => !p.hidden)?.querySelector('[data-track]'); }

    slide(dir) {
      const t = this.track();
      if (!t) return;
      const card = t.querySelector('.et-pc');
      const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(t).columnGap || 0) : t.clientWidth;
      t.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    arrows() {
      const t = this.track(), prev = this.querySelector('[data-prev]'), next = this.querySelector('[data-next]');
      if (!t || !prev) return;
      prev.disabled = t.scrollLeft <= 2;
      next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
    }

    async add(form) {
      const btn = form.querySelector('button');
      btn.classList.add('is-loading');
      const base = routes().cartAdd || '/cart/add';
      try {
        const res = await fetch(base.replace(/\.js$/, '') + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!res.ok) throw new Error(res.status);
        const cart = await (await fetch((routes().cart || '/cart') + '.js', { headers: { Accept: 'application/json' } })).json();
        document.dispatchEvent(new CustomEvent('et:cart:updated', { detail: cart }));
        btn.classList.add('is-done');
        setTimeout(() => btn.classList.remove('is-done'), 1800);
        if (!(window.EtarCart && window.EtarCart.afterAdd(cart, btn))) this.say(this.dataset.added, true);
      } catch (err) {
        this.say(this.dataset.error, false);
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    say(msg, withLink) {
      if (!this.toast) return;
      this.toast.innerHTML = '';
      const span = document.createElement('span'); span.textContent = msg; this.toast.appendChild(span);
      if (withLink) { const a = document.createElement('a'); a.href = routes().cart || '/cart'; a.textContent = this.dataset.viewBag; this.toast.appendChild(a); }
      this.toast.hidden = false;
      requestAnimationFrame(() => this.toast.classList.add('is-on'));
      clearTimeout(this.toastT);
      this.toastT = setTimeout(() => { this.toast.classList.remove('is-on'); setTimeout(() => { this.toast.hidden = true; }, 450); }, 3200);
    }
  }
  customElements.define('et-products', EtProducts);
})();
